<script setup lang="ts">
import QueueItems from "~/components/queue/QueueItems.vue";
import QueueWidgets from "~/components/queue/QueueWidgets.vue";

const { t } = useI18n();

const code: string = useRoute().params.code as string;

const store = useCheckpointDetailsStore();
const {
  checkpointSummary: summary,
  summaryPending: summaryPending,
  checkpointDetails: details,
  checkpointDetailsItemsLimit: itemsLimit,
  checkpointDetailsPending: detailsPending,
} = storeToRefs(store);

if (import.meta.server && ! await store.fetchSummaryData(code)) {
  throw createError({
    statusCode: 404,
    statusMessage: t('checkpoint_details.page_title_no_checkpoint')
  });
}

const { status: dataStatus } = await useLazyAsyncData(async () => {
  await store.fetchSummaryData(code);

  if (summary.value?.hasQueues) {
    await store.fetchDetailsData(code);
  }

  return true;
});

if (import.meta.client) {
  const tryDetailsPolling = () => {
    if (summary.value?.hasQueues) {
      store.startDetailsPooling(code);
    }
  };

  if (summary.value) {
    // ssr opening of the page, summary.value is already exists
    tryDetailsPolling();
  } else {
    // wait until data is loaded
    const unWatch = watch(dataStatus, (status) => {
      if (status !== 'success') {
        return;
      }

      tryDetailsPolling();
      unWatch();
    });
  }
}

const isItemsQueueExpanded = ref<boolean>(false);

const onItemsQueueExpanded = () => {
  isItemsQueueExpanded.value = true;
};

const pageTitle = computed<string>(() => {
  return summary.value
      ? t('checkpoint_details.page_title', {name: summary.value.name})
      : (
          summaryPending.value
              ? t('page_title_data_loading')
              : t('checkpoint_details.page_title_no_checkpoint')

      )
});
const pageDescription = computed<string | null>(() => {
  return summary.value
      ? t('checkpoint_details.page_description', {name: summary.value.name})
      : null
});

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogImage: absoluteUrl(`/images/checkpoints/${code}.jpg`),
});

onUnmounted(() => {
  store.clearData();
});

const onItemsLoadingRequested = (limit: number) => {
  store.restartDetailsPooling(code, limit);
}
</script>

<template>
  <div
    v-if="summary"
    class="container-fluid"
  >
    <div class="row border-0 border-md rounded">
      <div class="col-12 col-md-6 col-lg-4 pt-3 pb-0 pb-md-3 px-0 px-md-3 position-relative">
        <NuxtLink
          :to="{name: 'index'}"
          class="btn btn-md bg-body-secondary position-absolute ms-2 mt-2"
          :title="t('checkpoint_details.back_to_list')"
        >
          <i class="icon icon-lg cil-arrow-circle-left mt-1" />
        </NuxtLink>
        <img
          class="object-fit-fill border rounded w-100"
          :src="`/images/checkpoints/${summary.code}.jpg`"
          :alt="summary.name"
        >
      </div>
      <div class="col-12 col-md-6 col-lg-8 col-xl-8 py-2 py-md-3 px-0 px-md-3">
        <h1>
          {{ summary.name }}
        </h1>
        <h6 v-if="summary.total">
          <span v-tooltip="summary.hasQueues ? t('checkpoint_details.total_number.title') : t('checkpoint_details.total_number.title_no_wz')">
            {{ t("checkpoint_details.total_number.text", {total: summary!.total!.number}) }}
          </span>
          <sub
            v-tooltip="t('checkpoint_details.total_number.note', {time: summary!.total!.date.toFormat('HH')})"
            class="total-date ms-1"
          >
            <i class="cil-clock icon" /> {{ summary!.total!.date.toFormat('HH:mm') }}
          </sub>
        </h6>
        <h3
          v-if="summary.isClosed"
          class="text-danger"
        >
          {{ t('checkpoint_details.neighboring_checkpoint_closed') }}
        </h3>
      </div>
    </div>

    <div
      v-if="summary.hasQueues"
      class="mt-0 mt-md-4"
    >
      <div
        v-if="details?.carsQueues"
        class="row pt-md-3 border-0 border-md rounded"
      >
        <div :class="isItemsQueueExpanded ? 'col-12 px-0 px-md-3 mb-3' : 'col-12 col-md-6 col-lg-4 col-xxl-3 px-0 px-md-3 mb-3'">
          <QueueItems
            :counts="details.carsQueues.counts"
            :items="details.carsQueues.items"
            :items-limit="itemsLimit"
            :is-loading="detailsPending"
            @expanded="onItemsQueueExpanded"
            @collapsed="isItemsQueueExpanded = false"
            @loading-requested="onItemsLoadingRequested"
          />
        </div>
        <QueueWidgets
          v-if="details.carsQueues.live.hourData"
          widget-class="col-12 col-md-6 col-lg-4 col-xxl-3 px-0 px-md-3 mb-3"
          :queue-hour-data="details.carsQueues.live.hourData"
        />
      </div>
      <div
        v-else-if="detailsPending"
        class="text-center"
      >
        <div
          class="spinner-border text-secondary"
          role="status"
        >
          <span class="visually-hidden">{{ t('loading_') }}</span>
        </div>
      </div>
      <div
        v-else
        class="text-center"
      >
        <h3>{{ t('error.no_data_loaded') }}</h3>
        <button
          class="btn btn-primary"
          @click="store.fetchDetailsData(code)"
        >
          {{ t('error.try_again') }}
        </button>
      </div>
    </div>
  </div>
  <div
    v-else-if="summaryPending"
    class="row g-4"
  >
    <div class="text-center">
      <div
        class="spinner-border text-secondary"
        role="status"
      >
        <span class="visually-hidden">{{ t('loading_') }}</span>
      </div>
    </div>
  </div>
  <ErrorNotFound
    v-else
    :message="t('checkpoint_details.page_title_no_checkpoint')"
  />
</template>